import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// Third-party brand colours. Deliberately local rather than in REDESIGN_COLORS:
// they belong to Google and Orange, not to this design system.
const BRAND = {
    classroomText: '#4B635F',
    orange: '#F58220',
};

// `href` makes the whole card a link: the Samsung event coverage, the Teams
// store listing and the Orange Fab partnership page. Classroom has no page.
const CARDS = [
    {
        key: 'samsung',
        logo: '/redesign/proof-samsung.png',
        logoSize: [512, 200],
        alt: 'Samsung',
        logoHeight: 34,
        href: 'https://www.edupedu.ro/video-profesorul-poate-vedea-pe-tabla-interactiva-toti-elevii-separat-si-pentru-fiecare-poate-evalua-ceea-ce-face-isi-pot-descarca-ce-au-lucrat-dar-lectia-ramane-in-aplicatie-si-nu-mai-e-nevoie-de-s/',
    },
    {
        key: 'teams',
        logo: '/redesign/proof-microsoft-teams.png',
        logoSize: [240, 136],
        alt: 'Microsoft Teams',
        logoHeight: 52,
        href: 'https://appsource.microsoft.com/en-us/product/office/WA200004360?tab=Overview',
    },
    {
        key: 'classroom',
        logo: '/redesign/proof-google-classroom.svg',
        logoSize: [108, 108],
        alt: '',
        logoHeight: 42,
        wordmark: 'Google Classroom',
    },
    {
        key: 'orange',
        logoHeight: 34,
        textLogo: 'Digitaliada · Orange',
        href: 'https://www.orangefab.ro/vboard-orange-fab-startups-that-supports-online-education/',
    },
];

const cx = (...names) => names.filter(Boolean).join(' ');

const useStyles = makeStyles(() => ({
    lead: {
        margin: '0 0 28px',
        color: REDESIGN_COLORS.body,
        textAlign: 'center',
        fontSize: '0.82rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 470px), 1fr))',
        gap: '26px',
    },
    card: {
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        alignContent: 'center',
        gap: '20px',
        minHeight: '230px',
        padding: '44px 32px',
        borderRadius: '26px',
        background: REDESIGN_COLORS.white,
        textAlign: 'center',
    },
    // A card that opens the partner's page: same look, a lift on hover and a
    // link line at the bottom.
    cardLink: {
        color: 'inherit',
        textDecoration: 'none',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover, &:focus-visible': {
            transform: 'translateY(-3px)',
            boxShadow: '0 18px 40px rgba(6, 63, 69, 0.10)',
        },
        '&:hover $link, &:focus-visible $link': {
            textDecoration: 'underline',
        },
    },
    link: {
        color: REDESIGN_COLORS.link,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    logo: {
        width: 'auto',
        objectFit: 'contain',
    },
    wordmarkRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    wordmarkIcon: {
        width: '42px',
        height: '42px',
        objectFit: 'contain',
    },
    wordmark: {
        color: BRAND.classroomText,
        fontSize: '1.15rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '-0.025em',
    },
    textLogo: {
        display: 'flex',
        alignItems: 'center',
        color: BRAND.orange,
        fontSize: '1.35rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '-0.025em',
    },
    title: {
        color: REDESIGN_COLORS.dark,
        fontSize: '1.15rem',
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
    },
    text: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.5,
    },
}));

/**
 * The `#dovezi` band: the platforms VBoard already plugs into.
 */
const HomeProof = () => {
    const classes = useStyles();

    return (
        <LandingSection id="dovezi" paddingTop={32} paddingBottom={64}>
            <p className={classes.lead}>
                <FormattedMessage id="redesign.home.proof.lead" />
            </p>

            <div className={classes.grid}>
                {CARDS.map(({ key, logo, logoSize, alt, logoHeight, wordmark, textLogo, href }) => {
                    const Card = href ? 'a' : 'article';
                    const linkProps = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};

                    return (
                        <Card key={key} className={cx(classes.card, href && classes.cardLink)} {...linkProps}>
                            {textLogo ? (
                                <span className={classes.textLogo} style={{ height: `${logoHeight}px` }}>
                                    {textLogo}
                                </span>
                            ) : wordmark ? (
                                <span className={classes.wordmarkRow} style={{ height: `${logoHeight}px` }}>
                                    <img
                                        src={logo}
                                        alt={alt}
                                        width={logoSize && logoSize[0]}
                                        height={logoSize && logoSize[1]}
                                        className={classes.wordmarkIcon}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <span className={classes.wordmark}>{wordmark}</span>
                                </span>
                            ) : (
                                <img
                                    src={logo}
                                    alt={alt}
                                    width={logoSize && logoSize[0]}
                                    height={logoSize && logoSize[1]}
                                    className={classes.logo}
                                    style={{ height: `${logoHeight}px` }}
                                    loading="lazy"
                                    decoding="async"
                                />
                            )}

                            <strong className={classes.title}>
                                <FormattedMessage id={`redesign.home.proof.${key}.title`} />
                            </strong>
                            <span className={classes.text}>
                                <FormattedMessage id={`redesign.home.proof.${key}.text`} />
                            </span>
                            {href ? (
                                <span className={classes.link}>
                                    <FormattedMessage id={`redesign.home.proof.${key}.link`} />
                                </span>
                            ) : null}
                        </Card>
                    );
                })}
            </div>
        </LandingSection>
    );
};

export default HomeProof;
